import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useSalesStore } from './sales'
import { useEISInventoryStore } from './inventory'
import { useAuthStore } from './useAuthStore'
import { ROLES } from '@/router'

export interface DashboardMetric {
  title: string
  value: string
  icon: string
  color: 'success' | 'error' | 'warning' | 'info' | 'primary' | 'secondary'
  trend?: {
    value: number
    direction: 'up' | 'down' | 'neutral'
    isPositive: boolean
  }
  subtitle?: string
}

export interface AlertItem {
  id: string
  type: 'error' | 'warning' | 'info' | 'success'
  title: string
  message: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  link: string
}

export const useDashboardStore = defineStore('dashboard', () => {
  const salesStore = useSalesStore()
  const inventoryStore = useEISInventoryStore()
  const authStore = useAuthStore()

  // State
  const loading = ref(false)
  const error = ref<string | null>(null)
  const lastUpdated = ref<Date | null>(null)

  // Derive role once
  const isCashier = computed(() => authStore.user?.role?.toLowerCase() === ROLES.CASHIER)

  // Get currency from config
  let config: { Currency?: string } = {}
  try {
    if (authStore.configuration) {
      config = JSON.parse(authStore.configuration)
    }
  } catch (e) {
    console.error('Invalid configuration JSON', e)
  }
  const currency = config.Currency || 'MWK'

  const dashboardMetrics = computed((): DashboardMetric[] => {
    const summary = salesStore.salesSummary
    if (!summary) return []

    const onlinePercentage =
      summary.totalSales > 0 ? (summary.onlineSalesAmount / summary.totalSales) * 100 : 0

    return [
      {
        title: 'Total Sales',
        value: `${currency} ${summary.totalSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        icon: 'mdi-cash-multiple',
        color: 'success',
        subtitle: `${summary.totalTransactions} transactions`,
      },
      {
        title: 'Average Transaction',
        value: `${currency} ${summary.averageTransaction.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        icon: 'mdi-cart',
        color: 'primary',
        subtitle: 'Per transaction',
      },
      {
        title: 'Online Sales',
        value: `${summary.onlineSales}`,
        icon: 'mdi-cloud-check',
        color: 'info',
        trend: {
          value: onlinePercentage,
          direction: onlinePercentage >= 50 ? 'up' : 'down',
          isPositive: true,
        },
        subtitle: `${currency} ${summary.onlineSalesAmount.toLocaleString()}`,
      },
      {
        title: 'Offline Sales',
        value: `${summary.offlineSales}`,
        icon: 'mdi-cloud-off-outline',
        color: 'warning',
        subtitle: `${currency} ${summary.offlineSalesAmount.toLocaleString()}`,
      },
      {
        title: 'Total Tax',
        value: `${currency} ${summary.totalTax.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        icon: 'mdi-file-document',
        color: 'secondary',
        subtitle: 'VAT collected',
      },
      {
        title: 'Discounts Given',
        value: `${currency} ${summary.totalDiscounts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        icon: 'mdi-tag-percent',
        color: 'error',
        subtitle: 'Total discounts',
      },
    ]
  })

  const inventoryMetrics = computed((): DashboardMetric[] => {
    // Cashiers don't see inventory metrics
    if (isCashier.value) return []

    const value = inventoryStore.inventoryValue
    if (!value) return []

    return [
      {
        title: 'Total Products',
        value: value.totalProducts.toString(),
        icon: 'mdi-package-variant',
        color: 'primary',
        subtitle: `${value.totalQuantity.toLocaleString()} units`,
      },
      {
        title: 'Inventory Value',
        value: `${currency} ${value.totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        icon: 'mdi-currency-usd',
        color: 'success',
        subtitle: 'Total stock value',
      },
      {
        title: 'Low Stock',
        value: value.lowStockCount.toString(),
        icon: 'mdi-alert',
        color: 'warning',
        subtitle: 'Items below minimum',
      },
      {
        title: 'Out of Stock',
        value: value.outOfStockCount.toString(),
        icon: 'mdi-close-circle',
        color: 'error',
        subtitle: 'Items unavailable',
      },
    ]
  })

  const alerts = computed((): AlertItem[] => {
    // Cashiers only see offline sync alerts
    if (isCashier.value) {
      const alertList: AlertItem[] = []
      const pendingOffline = salesStore.offlineSales.filter((s) => !s.isPushed).length
      if (pendingOffline > 0) {
        alertList.push({
          id: 'offline-pending',
          type: 'warning',
          title: 'Offline Sales Pending',
          message: `${pendingOffline} offline sales waiting to sync`,
          priority: 'medium',
          link: '/sales/offline',
        })
      }
      return alertList
    }

    // Admin sees all alerts
    const alertList: AlertItem[] = []
    const inventory = inventoryStore.inventoryValue
    const offlineSales = salesStore.offlineSales

    if (inventory) {
      if (inventory.outOfStockCount > 0) {
        alertList.push({
          id: 'out-of-stock',
          type: 'error',
          title: 'Out of Stock Items',
          message: `${inventory.outOfStockCount} products are out of stock`,
          priority: 'critical',
          link: '/inventory/low-stock',
        })
      }
      if (inventory.lowStockCount > 0) {
        alertList.push({
          id: 'low-stock',
          type: 'warning',
          title: 'Low Stock Warning',
          message: `${inventory.lowStockCount} products are running low`,
          priority: 'high',
          link: '/inventory/low-stock',
        })
      }
    }

    const pendingOffline = offlineSales.filter((s) => !s.isPushed).length
    if (pendingOffline > 0) {
      alertList.push({
        id: 'offline-pending',
        type: 'warning',
        title: 'Offline Sales Pending',
        message: `${pendingOffline} offline sales waiting to sync`,
        priority: 'medium',
        link: '/sales/offline',
      })
    }

    return alertList
  })

  // Actions
  const loadDashboardData = async (startDate?: string, endDate?: string) => {
    loading.value = true
    error.value = null

    try {
      const cashier = isCashier.value

      // Sales summary — backend filters by cashier when isCashier=true
      await salesStore.getSalesSummary(startDate, endDate, cashier)

      // Top products (cashier sees only their own via isCashier flag)
      await salesStore.getTopProducts(startDate, endDate, 10, cashier)

      // Online / offline overview
      await salesStore.getOnlineSales(startDate, endDate, 1, 10, cashier)
      await salesStore.getOfflineSales(startDate, endDate, undefined, 1, 10, cashier)

      // Inventory — skip entirely for cashiers
      if (!cashier) {
        await inventoryStore.getInventoryValue()
        await inventoryStore.getLowStockProducts()
      }

      lastUpdated.value = new Date()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load dashboard'
      console.error('Dashboard load error:', err)
    } finally {
      loading.value = false
    }
  }

  const refreshDashboard = async () => {
    const today = new Date()
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    await loadDashboardData(startOfMonth.toISOString(), today.toISOString())
  }

  const clearError = () => {
    error.value = null
  }

  const getSalesChartData = computed(() => {
    const topProducts = salesStore.topProducts
    return {
      labels: topProducts.map((p) => p.productName),
      datasets: [
        {
          label: 'Revenue',
          data: topProducts.map((p) => p.totalRevenue),
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
        },
      ],
    }
  })

  const getOnlineVsOfflineData = computed(() => {
    const summary = salesStore.salesSummary
    if (!summary) return null

    return {
      labels: ['Online', 'Offline'],
      datasets: [
        {
          data: [summary.onlineSalesAmount, summary.offlineSalesAmount],
          backgroundColor: ['#4CAF50', '#FF9800'],
        },
      ],
    }
  })

  return {
    loading,
    error,
    lastUpdated,
    currency,
    isCashier,

    dashboardMetrics,
    inventoryMetrics,
    alerts,
    getSalesChartData,
    getOnlineVsOfflineData,

    loadDashboardData,
    refreshDashboard,
    clearError,
  }
})
